/**
 * Utilidad centralizada para normalización de respuestas de API
 * Responsabilidad única: Transformar todas las claves de objetos a minúsculas
 * Principio SOLID aplicado - Single Responsibility Principle
 */

/**
 * Normaliza recursivamente todas las claves de un objeto/array a minúsculas
 *
 * @param data - Datos a normalizar (puede ser objeto, array, primitivo)
 * @returns Datos normalizados con claves en minúsculas
 *
 * @example
 * normalizeResponseKeys({ CPAIS: 'VE', NLocaliza: '123' })
 * // => { cpais: 'VE', nlocaliza: '123' }
 *
 * @example
 * normalizeResponseKeys([{ CDoctOR: 'A1', XNomBRE: 'Juan' }])
 * // => [{ cdoctor: 'a1', xnombre: 'Juan' }]
 */
export const normalizeResponseKeys = <T = any>(data: T): T => {
  // Si es null o undefined, retornar tal cual
  if (data === null || data === undefined) {
    return data;
  }

  // Si es un array, normalizar cada elemento
  if (Array.isArray(data)) {
    return data.map(item => normalizeResponseKeys(item)) as any;
  }

  // Si es un objeto (no Date, no RegExp, etc.), normalizar sus claves
  if (typeof data === 'object' && data.constructor === Object) {
    const normalized: any = {};

    Object.keys(data).forEach((key) => {
      const normalizedKey = key.toLowerCase();
      const value = (data as any)[key];

      // Normalizar recursivamente el valor
      normalized[normalizedKey] = normalizeResponseKeys(value);
    });

    return normalized;
  }

  // Para primitivos (string, number, boolean) y otros tipos, retornar tal cual
  return data;
};

/**
 * Verifica si un dato necesita normalización (tiene claves en mayúsculas)
 * Útil para evitar procesamiento innecesario
 *
 * @param data - Datos a verificar
 * @returns true si tiene claves que no están en minúsculas
 */
export const needsNormalization = (data: any): boolean => {
  if (!data || typeof data !== 'object') {
    return false;
  }

  if (Array.isArray(data)) {
    return data.some(item => needsNormalization(item));
  }

  return Object.keys(data).some(key => key !== key.toLowerCase());
};

/**
 * Normaliza solo si es necesario (optimización de performance)
 *
 * @param data - Datos a normalizar
 * @returns Datos normalizados (o los originales si no necesitan normalización)
 */
export const normalizeIfNeeded = <T = any>(data: T): T => {
  if (!needsNormalization(data)) {
    return data;
  }

  return normalizeResponseKeys(data);
};

/**
 * Convierte una fecha en formato YYYYMMDD a dd/mm/yyyy.
 * Si el valor no es una fecha válida en ese formato, lo retorna sin cambios.
 *
 * @param value - Valor a convertir
 * @returns Fecha formateada dd/mm/yyyy o el valor original
 *
 * @example
 * formatSigmaDate('20260317') // => '17/03/2026'
 * formatSigmaDate('')         // => ''
 * formatSigmaDate('abc')      // => 'abc'
 */
export const formatSigmaDate = (value: string): string => {
  if (!value) return value;
  if (/^\d{16}$/.test(value)) {
    const year  = value.slice(0, 4);
    const month = value.slice(4, 6);
    const day   = value.slice(6, 8);
    const hour  = value.slice(8, 10);
    const min   = value.slice(10, 12);
    return `${day}/${month}/${year} ${hour}:${min}`;
  }
  if (/^\d{8}$/.test(value)) {
    const year  = value.slice(0, 4);
    const month = value.slice(4, 6);
    const day   = value.slice(6, 8);
    return `${day}/${month}/${year}`;
  }
  return value;
};

/**
 * Aplica formatSigmaDate sobre los campos indicados de un objeto o de objetos anidados.
 * Recorre recursivamente arrays y objetos.
 *
 * @param data    - Objeto/array/valor a procesar
 * @param fields  - Conjunto de nombres de campo (en cualquier capitalización) a formatear
 * @returns Nuevo objeto con las fechas convertidas a dd/mm/yyyy
 *
 * @example
 * normalizeDateFields(response, ['FCITA', 'FNACIMIENTO', 'FDESDE_POL'])
 */
export const normalizeDateFields = <T = any>(data: T, fields: string[]): T => {
  if (data === null || data === undefined) return data;

  if (Array.isArray(data)) {
    return data.map(item => normalizeDateFields(item, fields)) as any;
  }

  if (typeof data === 'object' && data.constructor === Object) {
    const fieldSet = new Set(fields.map(f => f.toLowerCase()));
    const result: any = {};

    Object.keys(data).forEach(key => {
      const value = (data as any)[key];
      if (fieldSet.has(key.toLowerCase()) && typeof value === 'string') {
        result[key] = formatSigmaDate(value);
      } else {
        result[key] = normalizeDateFields(value, fields);
      }
    });

    return result;
  }

  return data;
};
